import _                                                                                                                        from 'underscore';
import React, { useContext }                                                                                                    from 'react';
import { useLocation, }                                                                                                         from 'react-router-dom';
import { CurrentUserProfileParamsReviewSection, PlanningAreaParamsSection, ProjectManagementParamsSettingsSection, Size, Uuid } from '../types';
import { Icon, Popup }                                                                                                          from 'semantic-ui-react';
import { usePrompts }                                                                                                           from './usePrompts';
import { useCurrentUser }                                                                                                       from './useAccount';
import { RouterUtility }                                                                                                        from '../util';
import { RolePolicyType }                                                                                                       from '../services/RoleAccessService';
import { ENTITY_ICONS, ORGANIZATIONAL_ROLES }                                                                                   from '../constants/ProductEnums';
import { ProjectOperationsMenu }                                                                                                from '../views/pages/projects/operations/ProjectOperationsMenu';
import { StyledContainer }                                                                                                      from '../views/containers';
import { GenericNavItem }                                                                                                       from '../views/components/nav/GenericNav';
import { useUIDisplayModes }                                                                                                    from './useUIDisplayModes';
import { useFeatureFlags }                                                                                                      from './useFeatureFlags';
import { useSystemSettings }                                                                                                    from "@src/hooks/useSystemSettings";
import { ProjectContext }                                                                                                       from "@src/views/pages/projects/ProjectIndexPage";


type NavObjectsGeneratorType<T = Uuid> = ( projectId?: T ) => GenericNavItem[];
type NavObjectsGeneratorWithOptionsType<T> = ( options: T ) => GenericNavItem[];

interface UsePageNavsHooks {
    generateProjectLevelNav: NavObjectsGeneratorWithOptionsType<{ projectId: Uuid, includeChildren: boolean }>
    generateProjectManagementSectionNav: NavObjectsGeneratorType
    generateObjectiveSectionNav: NavObjectsGeneratorType
    generateProjectMappingAreasNav: NavObjectsGeneratorType
    generateResourcePlanningSectionNav: NavObjectsGeneratorType
    generateSystemAccountingSectionNav: NavObjectsGeneratorType
    generateUserAccountSectionNav: NavObjectsGeneratorType
    generateAdminSectionNav: NavObjectsGeneratorWithOptionsType<{ DEMLink: boolean, accountslink: boolean }>
    generateDecisionEngineSectionNav: NavObjectsGeneratorType
    generateToolsSectionNav: NavObjectsGeneratorType
    generateHelpSectionNav: NavObjectsGeneratorType
}

function usePageNavs(): UsePageNavsHooks{

    let { pathname }                                                      = useLocation(),
        { hasProjectRoleAccessTo, isSystemAdmin, hasRoleInOrganizations } = useCurrentUser(),
        { minimizedChrome }                                               = useUIDisplayModes(),
        { promptSupportActions }                                          = usePrompts(),
        { showSystemSettings }                                            = useSystemSettings(),
        featureFlags                                                      = useFeatureFlags();

    const generateProjectLevelNav: UsePageNavsHooks['generateProjectLevelNav'] = ( options ) => {

        let canViewProjectPlanning  = featureFlags.permissionProjectPlanningView,
            canViewResourcePlanning = featureFlags.permissionProjectResourcePlanningView,
            canViewTools            = featureFlags.permissionProjectToolsView,
            canUseOperations        = true,
            canViewReports          = featureFlags.enableReportsPageView;

        return _.compact( [

            // PROJECT MANAGEMENT
            // ------------------------------------------------------------------------------------------------
            /*canUpdateSettings ? {
             label:    'Project',
             path:     'PROJECT.MANAGE.SETTINGS',
             params:   { projectId: options.projectId },
             isParent: false,
             ...( options.includeChildren ? { subItems: generateProjectManagementSectionNav( options.projectId ) } : {} )
             } : null,*/

            // PROJECT PLANNING AREA
            // ------------------------------------------------------------------------------------------------
            // ( canViewProjectPlanning && {
            //     label:    'Planning',
            //     path:     'PROJECT.PLANNING',
            //     params:   { projectId: options.projectId, section: PlanningAreaParamsSection.QUESTIONS_BY_CATEGORY },
            //     activeIf: () => {
            //
            //         // let the parent still be active, even though this url always drives to a child
            //         let baseRoute      = RouterUtility.getRoute( 'PROJECT.PLANNING', { projectId: options.projectId } ),
            //             foundBaseRoute = ( pathname.search( baseRoute ) !== -1 );
            //
            //         return foundBaseRoute;
            //     },
            //     isParent: false,
            //     ...( options.includeChildren ? { subItems: [] } : {} )
            // } ),

            // MAPPING / VISUALIZATION GROUP
            // ------------------------------------------------------------------------------------------------
            {
                label:    'Maps',
                path:     'PROJECT.OBJECTIVES.ROADMAP',
                params:   { projectId: options.projectId },
                activeIf: () => {

                    // let the menu item still be active when we're in a different map
                    let childRoutes = [
                        RouterUtility.getRoute( 'PROJECT.MAP', { projectId: options.projectId } ),
                        RouterUtility.getRoute( 'PROJECT.MAP.TYPES.GANTT', { projectId: options.projectId } ),
                        RouterUtility.getRoute( 'PROJECT.OBJECTIVES.ROADMAP', { projectId: options.projectId } ),
                    ];

                    return ( childRoutes.includes( pathname ) );
                },
                isParent: false,
                ...( options.includeChildren ? { subItems: [] } : {} )
                // ...( options.includeChildren ? { subItems: generateProjectMappingAreasNav( options.projectId ) } : {} )
            },
            ( canViewReports &&
              {
                  label:    'Dashboard',
                  path:     'PROJECT.DASH',
                  params:   { projectId: options.projectId },
                  activeIf: () => {

                      // let the menu item still be active when we're in a different map
                      let childRoutes = [
                          RouterUtility.getRoute( 'PROJECT.DASH', { projectId: options.projectId } ),
                      ];

                      return ( childRoutes.includes( pathname ) );
                  },
                  isParent: false,
                  ...( options.includeChildren ? { subItems: [] } : {} )
                  // ...( options.includeChildren ? { subItems: generateProjectMappingAreasNav( options.projectId ) } : {} )
              } ),
            // RESOURCE PLANNING GROUP
            // ------------------------------------------------------------------------------------------------
            ( canViewResourcePlanning && {
                label:    'Budget',
                path:     'PROJECT.RESOURCE_PLANNING.REPORTS.SUMMARY_VISUALIZATION',
                params:   { projectId: options.projectId },
                activeIf: () => {

                    // let the parent still be active, even though this url always drives to a child
                    let baseRoute      = RouterUtility.getRoute( 'PROJECT.RESOURCE_PLANNING', { projectId: options.projectId } ),
                        foundBaseRoute = ( pathname.search( baseRoute ) !== -1 );

                    return foundBaseRoute;
                },
                isParent: false,
                ...( options.includeChildren ? { subItems: [] } : {} )
                // ...( options.includeChildren ? { subItems: generateResourcePlanningSectionNav( options.projectId ) } : {} )
            } ),

            // TOOLS GROUP
            // ------------------------------------------------------------------------------------------------
            ( canViewTools && {
                label:    'Tools',
                path:     'PROJECT.TOOLS.SEARCH.ACTIVITY_EDITABLE_SEARCH',
                params:   { projectId: options.projectId },
                activeIf: () => {

                    // let the parent still be active, even though this url always drives to a child
                    let baseRoute      = RouterUtility.getRoute( 'PROJECT.TOOLS', { projectId: options.projectId } ),
                        foundBaseRoute = ( pathname.search( baseRoute ) !== -1 );

                    return foundBaseRoute;
                },
                isParent: false,
                ...( options.includeChildren ? { subItems: [] } : {} )
                //...( options.includeChildren ? { subItems: generateToolsSectionNav( options.projectId ) } : {} )
            } ),

            // OPERATIONS MENU
            // ------------------------------------------------------------------------------------------------
            ( canUseOperations && {
                label:    <Popup
                              openOnTriggerMouseEnter={ false }
                              openOnTriggerClick={ true }
                              openOnTriggerFocus={ false }
                              closeOnTriggerMouseLeave={ false }
                              closeOnPortalMouseLeave={ true }
                              position={ 'bottom right' }
                              className={ 'no-padding-bottom' }
                              offset={ 10 }
                              trigger={ (
                                  <StyledContainer padding={ Size.XS } className={ 'cursor--pointer' }>
                                      <Icon name={ 'cog' } fitted/>
                                  </StyledContainer>
                              ) }
                              content={
                                  <ProjectOperationsMenu
                                      projectId={ options.projectId }
                                      showLogout={ false }
                                      showSwitchProjects={ false }
                                      showRemap={ false }
                                      showExport={ false }
									  showVersionControl={ featureFlags.enableAdHocProjectVersionControlTool }
                                      showRollUpVersions={ true }
									  showTemplateCurrentProject={ featureFlags.enableProjectTemplating && isSystemAdmin() }
                                      showTemplateManagement={ featureFlags.enableProjectTemplating && isSystemAdmin() }
                                      showCreateProject={ featureFlags.enableProjectCreateFromTemplate && hasRoleInOrganizations( ORGANIZATIONAL_ROLES.ADMIN )?.hasRole }
                                      useDropdown={ false }
                                      containerProps={ {
                                          vertical:   true,
                                          borderless: true,
                                          text:       true,
                                          size:       'small',
                                          className:  'no-margin no-border no-box-shadow text-left'
                                      } }
                                  />
                              }/>,
                path:     null,
                params:   { projectId: options.projectId },
                isParent: false,
            } ),

            // CORVAL COACH
            // ------------------------------------------------------------------------------------------------
            /*{
             label:    <Button circular color={ SemanticUIColor.PURPLE } size={ SemanticUISize.TINY } className={ 'no-margin' }>Corval Coach</Button>,
             path:     null,
             isParent: false,
             ...( options.includeChildren ? { subItems: [] } : {} )
             }*/

            // UTIL MENU
            // ------------------------------------------------------------------------------------------------
            /*{
             label:    <Icon name={ 'cog' } fitted/>,
             path:     null,
             isParent: false,
             ...( options.includeChildren ? {
             subItems: [
             ...generateProjectManagementSectionNav( options.projectId ),
             ...generateHelpSectionNav( options.projectId ),
             ]
             } : {} )
             },*/
        ] )
    }

    const generateProjectManagementSectionNav: UsePageNavsHooks['generateProjectManagementSectionNav'] = ( projectId ) => {

        let canUpdateSettings = hasProjectRoleAccessTo( projectId, [ RolePolicyType.PROJECT_MANAGE_ACCESS ] );

        return _.compact( [

            /*{
             label:     'Activity',
             icon:      { name: 'clock outline' },
             path:      'PROJECT.MANAGE.ACTIVITY',
             params:    { projectId },
             isParent:  false,
             isDefault: true,
             },*/

            ( canUpdateSettings && {
                label:     'Project Settings',
                icon:      { name: 'briefcase' },
                path:      'PROJECT.MANAGE.SETTINGS',
                params:    { projectId, section: ProjectManagementParamsSettingsSection.DETAILS },
                isParent:  false,
                isDefault: false,
            } ),

        ] ) as GenericNavItem[];
    }

    const generateObjectiveSectionNav: UsePageNavsHooks['generateObjectiveSectionNav'] = ( projectId ) => {

        const canViewProjectPlanning       = featureFlags.permissionProjectPlanningView,
              canViewActivityMapsGanttView = featureFlags.permissionProjectActivityMapViewGantt;

        return _.compact( [

            ( canViewProjectPlanning && {
                label:     'Planning',
                icon:      { name: 'list' },
                path:      'PROJECT.PLANNING',
                params:    { projectId },
                isParent:  false,
                isDefault: true,
            } ),

            {
                label:     'Objective Map',
                icon:      { name: ENTITY_ICONS.SUBOBJECTIVES.VISUALIZATION },
                path:      'PROJECT.OBJECTIVES.ROADMAP',
                params:    { projectId },
                isParent:  false,
                isDefault: false,
            },

            ( canViewActivityMapsGanttView && {
                label:     `Activity Map`,
                icon:      { name: ENTITY_ICONS.ACTIVITIES.VISUALIZATION_GANTT },
                path:      'PROJECT.MAP.TYPES.GANTT',
                params:    { projectId },
                isParent:  false,
                isDefault: false,
            } ),

        ] ) as GenericNavItem[];
    }

    const generateProjectMappingAreasNav: UsePageNavsHooks['generateProjectMappingAreasNav'] = ( projectId ) => {

        const canViewProjectPlanning       = featureFlags.permissionProjectPlanningView,
              canViewActivityMapsGanttView = featureFlags.permissionProjectActivityMapViewGantt,
              hasMultipleMappingViews      = false;

        return _.compact( [

            ( canViewProjectPlanning && {
                label:     'Planning',
                icon:      { name: 'list' },
                path:      'PROJECT.PLANNING',
                params:    { projectId },
                isParent:  false,
                isDefault: true,
            } ),

            {
                label:     'Objective Map',
                icon:      { name: ENTITY_ICONS.SUBOBJECTIVES.VISUALIZATION },
                path:      'PROJECT.OBJECTIVES.ROADMAP',
                params:    { projectId },
                isParent:  false,
                isDefault: false,
            },

            ( canViewActivityMapsGanttView && {
                label:     `Activity Map${ hasMultipleMappingViews ? ' (Gantt)' : '' }`,
                icon:      { name: ENTITY_ICONS.ACTIVITIES.VISUALIZATION_GANTT },
                path:      'PROJECT.MAP.TYPES.GANTT',
                params:    { projectId },
                isParent:  false,
                isDefault: false,
            } ),

        ] ) as GenericNavItem[];
    }

    const generateResourcePlanningSectionNav: UsePageNavsHooks['generateResourcePlanningSectionNav'] = ( projectId ) => {

        const canViewResourcePlanning = featureFlags.permissionProjectResourcePlanningView;

        if( !canViewResourcePlanning ) return [];

        return [
            // {
            //     label:     'Budget Summary',
            //     icon:      { name: ENTITY_ICONS.BUDGET.REPORTING },
            //     path:      'PROJECT.RESOURCE_PLANNING.REPORTS.SUMMARY_VISUALIZATION',
            //     params:    { projectId },
            //     isParent:  false,
            //     isDefault: false,
            // },
            {
                label:     'Budget Details',
                icon:      { name: ENTITY_ICONS.BUDGET.ENTITY },
                path:      'PROJECT.RESOURCE_PLANNING.BUDGET_BY_DASHBOARD_DATA',
                params:    { projectId },
                isParent:  false,
                isDefault: false,
            },
            {
                label:     'Hiring Details',
                icon:      { name: ENTITY_ICONS.BUDGET.ENTITY_HIRING },
                path:      'PROJECT.RESOURCE_PLANNING.BUDGET_HIRING_DATA',
                params:    { projectId },
                isParent:  false,
                isDefault: false,
            },
            {
                label:     'Hiring Manager',
                icon:      { name: ENTITY_ICONS.RESOURCING.HIRES.ENTITY },
                path:      'PROJECT.RESOURCE_PLANNING.HIRING_MANAGER',
                params:    { projectId },
                isParent:  false,
                isDefault: true,
            },
        ] as GenericNavItem[];
    }

    const generateToolsSectionNav: UsePageNavsHooks['generateToolsSectionNav'] = ( projectId ) => {

        const canViewProjectTools = featureFlags.permissionProjectToolsView;

        if( !canViewProjectTools ) return [];

        return [
            {
                label:     'Activity Search',
                icon:      { name: ENTITY_ICONS.ACTIVITIES.ENTITY },
                path:      'PROJECT.TOOLS.SEARCH.ACTIVITY_EDITABLE_SEARCH',
                params:    { projectId },
                isParent:  false,
                isDefault: false,
            },
        ] as GenericNavItem[];
    }

    const generateHelpSectionNav: UsePageNavsHooks['generateHelpSectionNav'] = ( projectId ) => {

        return [
            {
                label:               'Corval Support',
                icon:                { name: ENTITY_ICONS.SYSTEM.SUPPORT },
                path:                null,
                isParent:            false,
                isDefault:           false,
                onNavigationClicked: () => promptSupportActions()
            }
        ] as GenericNavItem[];
    }

    const generateSystemAccountingSectionNav: UsePageNavsHooks['generateSystemAccountingSectionNav'] = () => {

        return [
            {
                label:     'Organizations',
                path:      'ADMIN.SYSTEM_ACCOUNTING.ORGANIZATIONS',
                isParent:  false,
                isDefault: true,
            },
            {
                label:     'Users',
                path:      'ADMIN.SYSTEM_ACCOUNTING.USERS',
                isParent:  false,
                isDefault: false,
            }
        ] as GenericNavItem[];
    }

    const generateUserAccountSectionNav: UsePageNavsHooks['generateUserAccountSectionNav'] = () => {

        return _.compact( [

            isSystemAdmin() ? {
                label:     'Admin',
                icon:      { name: ENTITY_ICONS.USER_ROLES.SYSTEM.CORVALADMIN },
                path:      'ADMIN',
                isParent:  false,
                isDefault: false,
            } : null,
            {
                label:     'Profile',
                icon:      { name: ENTITY_ICONS.USER.ENTITY },
                path:      'CURRENT_USER.DETAILS',
                params:    { section: CurrentUserProfileParamsReviewSection.DETAILS },
                isParent:  false,
                isDefault: true,
            },
            {
                label:               'Global Settings',
                icon:                { name: ENTITY_ICONS.SYSTEM.SETTINGS },
                path:                null,
                isParent:            false,
                isDefault:           false,
                onNavigationClicked: () => showSystemSettings()
            },
            {
                label:     'Logout',
                icon:      { name: ENTITY_ICONS.USER.LOGOUT },
                path:      'LOGOUT',
                isParent:  false,
                isDefault: false,
            },
        ] ) as GenericNavItem[];
    }

    const generateAdminSectionNav: UsePageNavsHooks['generateAdminSectionNav'] = ( options ) => {

        let navObjects = [
            {
                label:     'Home',
                path:      'HOME',
                isParent:  true,
                isDefault: false,
            }
        ];

        if( options.DEMLink ){
            navObjects.push( {
                label:     'Decision Engine Manager',
                path:      'ADMIN.DECISION_ENGINE',
                isParent:  false,
                isDefault: false,
            } );
        }

        if( options.accountslink ){
            navObjects.push( {
                label:     'Organization & User Management',
                path:      'ADMIN.SYSTEM_ACCOUNTING',
                isParent:  false,
                isDefault: false,
            } );
        }

        return navObjects as GenericNavItem[];
    }

    const generateDecisionEngineSectionNav: UsePageNavsHooks['generateDecisionEngineSectionNav'] = () => {

        return [
            {
                label:     <Icon name={ 'arrow circle left' } size={ 'large' } inverted fitted/>,
                path:      'ADMIN',
                isParent:  true,
                isDefault: false,
            },
            {
                label:     'Questions',
                path:      'ADMIN.DECISION_ENGINE.QUESTIONS',
                isParent:  false,
                isDefault: true,
            },
            {
                label:     'Objectives',
                path:      'ADMIN.DECISION_ENGINE.OBJECTIVES',
                isParent:  false,
                isDefault: false,
            },
            {
                label:     'Activities',
                path:      'ADMIN.DECISION_ENGINE.ACTIVITIES',
                isParent:  false,
                isDefault: false,
            },
            {
                label:     'Categories',
                path:      'ADMIN.DECISION_ENGINE.CATEGORIES',
                isParent:  false,
                isDefault: false,
            },
            {
                label:     'Assumptions',
                path:      'ADMIN.DECISION_ENGINE.ASSUMPTIONS',
                isParent:  false,
                isDefault: false,
            },
            {
                label:     'Resources',
                path:      'ADMIN.DECISION_ENGINE.RESOURCES',
                isParent:  false,
                isDefault: false,
            },
            {
                label:     'Timing',
                path:      'ADMIN.DECISION_ENGINE.TIMING',
                isParent:  false,
                isDefault: false,
            }
        ] as GenericNavItem[];
    }

    return {
        generateProjectLevelNav,
        generateProjectManagementSectionNav,
        generateObjectiveSectionNav,
        generateResourcePlanningSectionNav,
        generateProjectMappingAreasNav,
        generateSystemAccountingSectionNav,
        generateUserAccountSectionNav,
        generateAdminSectionNav,
        generateDecisionEngineSectionNav,
        generateToolsSectionNav,
        generateHelpSectionNav,
    }
}

export { usePageNavs };
